import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { environment } from '../../environments/environment';
import { BehaviorSubject, catchError, finalize, forkJoin, from, Observable, of, switchMap, throwError } from 'rxjs';
import { LoadingService } from '../services/loading.service';

export interface UploadProgress {
  fileIndex: number;
  fileName: string;
  progress: number; // 0–100
  status: 'pending' | 'uploading' | 'complete' | 'error';
  error?: string;
}

const CHUNK_SIZE = 5 * 1024 * 1024; // 5 MB

@Injectable({
  providedIn: 'root',
})
export class MediaService {
  private http = inject(HttpClient);
  private loading = inject(LoadingService);
  private apiUrl = environment.apiUrl;

  private uploadProgressSubject = new BehaviorSubject<UploadProgress[]>([]);
  uploadProgress$ = this.uploadProgressSubject.asObservable();

  private updateFileProgress(fileIndex: number, update: Partial<UploadProgress>): void {
    const current = this.uploadProgressSubject.value;
    const updated = current.map(p =>
      p.fileIndex === fileIndex ? { ...p, ...update } : p
    );
    this.uploadProgressSubject.next(updated);
  }

  resetProgress(): void {
    this.uploadProgressSubject.next([]);
  }

  private async uploadChunked(mediaId: number, file: File | Blob, fileName: string, fileIndex: number): Promise<void> {
    const totalChunks = Math.ceil(file.size / CHUNK_SIZE);

    for (let i = 0; i < totalChunks; i++) {
      const start = i * CHUNK_SIZE;
      const end = Math.min(start + CHUNK_SIZE, file.size);
      const chunk = file.slice(start, end);

      const formData = new FormData();
      formData.append('file', chunk, `${fileName}.part${i}`);
      formData.append('chunk_index', String(i));
      formData.append('total_chunks', String(totalChunks));

      await this.http.post(
        `${this.apiUrl}/monitoring/media/${mediaId}/upload-chunk/`,
        formData
      ).toPromise();

      const progress = Math.round(((i + 1) / totalChunks) * 100);
      this.updateFileProgress(fileIndex, { progress, status: 'uploading' });
    }

    // Complete the upload
    await this.http.post(
      `${this.apiUrl}/monitoring/media/${mediaId}/complete-upload/`,
      {}
    ).toPromise();

    this.updateFileProgress(fileIndex, { progress: 100, status: 'complete' });
  }

  uploadMedia(
    observationId: string,
    file: File,
    mediaType: 'audio' | 'image' | 'video',
    role: string,
    extraData: any = {},
    fileIndex: number = 0
  ) {
    const metadata = {
      media_type: mediaType,
      role,
      status: 1,
      file_name: file.name || 'untitled',
      ...extraData
    };

    const useChunked = file.size > CHUNK_SIZE;

    return this.http.post<any>(`${this.apiUrl}/monitoring/observation/${observationId}/media/`, metadata).pipe(
      switchMap((response) => {
        if (useChunked) {
          // Use chunked upload for large files
          this.updateFileProgress(fileIndex, {
            progress: 0,
            status: 'uploading',
            fileName: file.name,
          });
          return from(this.uploadChunked(response.id, file, file.name, fileIndex));
        }

        // Small files: use the existing presigned URL flow
        const isLocal = response.presigned_url.includes('local-upload');
        const headers = isLocal ? new HttpHeaders() : new HttpHeaders({ 'Content-Type': '' });

        this.updateFileProgress(fileIndex, { progress: 50, status: 'uploading', fileName: file.name });

        return this.http.put<any>(response.presigned_url, file, { headers }).pipe(
          switchMap(() => {
            this.updateFileProgress(fileIndex, { progress: 90, status: 'uploading' });
            return this.http.patch(`${this.apiUrl}/monitoring/media/${response.id}/`, {
              ...metadata,
              status: 2,
            });
          })
        );
      }),
      switchMap(() => {
        this.updateFileProgress(fileIndex, { progress: 100, status: 'complete' });
        return of(null);
      }),
      catchError(err => {
        this.updateFileProgress(fileIndex, { status: 'error', error: err?.message || 'Upload failed' });
        return throwError(() => err);
      })
    );
  }

  uploadAllMedia(
    observationId: string,
    audioFiles: any[] = [],
    imageFiles: (File & { role?: string })[] = [],
    videoFiles: (File & { role?: string })[] = []
  ) {
    const uploadRequests: Observable<any>[] = [];
    let fileIndex = 0;

    // Initialize progress entries
    const progressEntries: UploadProgress[] = [];

    for (const audio of audioFiles) {
      const idx = fileIndex++;
      const fileName = audio.name || 'recording.webm';
      progressEntries.push({ fileIndex: idx, fileName, progress: 0, status: 'pending' });

      // Prefer direct Blob/File reference over fetching blob URL
      const directBlob: Blob | File | null = audio._blob || audio._file || null;
      const url = audio.media_file;

      let request: Observable<any>;
      if (directBlob) {
        const file = new File([directBlob], fileName, { type: directBlob.type || 'audio/webm' });
        request = this.uploadMedia(observationId, file, 'audio', audio.role || 'journal_entry', {}, idx);
      } else if (url && url.startsWith('blob:')) {
        request = from(fetch(url).then((r) => r.blob())).pipe(
          switchMap((blob) => {
            const file = new File([blob], fileName, { type: 'audio/webm' });
            return this.uploadMedia(observationId, file, 'audio', audio.role || 'journal_entry', {}, idx);
          })
        );
      } else {
        continue;
      }

      uploadRequests.push(
        request.pipe(
          catchError(err => {
            console.error('Audio upload failed', err);
            return throwError(() => err);
          })
        )
      );
    }

    for (const file of imageFiles) {
      const idx = fileIndex++;
      const actualFile = (file as any).rawFile instanceof File ? (file as any).rawFile : file;
      const role = (file as any).role || 'animal_photo';
      if (actualFile instanceof File) {
        progressEntries.push({ fileIndex: idx, fileName: actualFile.name, progress: 0, status: 'pending' });
        uploadRequests.push(
          this.uploadMedia(observationId, actualFile, 'image', role, {}, idx).pipe(
            catchError(err => {
              console.error('Image upload failed', err);
              return throwError(() => err);
            })
          )
        );
      }
    }

    for (const file of videoFiles) {
      const idx = fileIndex++;
      const actualFile = (file as any).rawFile instanceof File ? (file as any).rawFile : file;
      const role = (file as any).role || 'animal_recording';
      if (actualFile instanceof File) {
        progressEntries.push({ fileIndex: idx, fileName: actualFile.name, progress: 0, status: 'pending' });
        uploadRequests.push(
          this.uploadMedia(observationId, actualFile, 'video', role, {}, idx).pipe(
            catchError(err => {
              console.error('Video upload failed', err);
              return throwError(() => err);
            })
          )
        );
      }
    }

    this.uploadProgressSubject.next(progressEntries);

    if (uploadRequests.length === 0) {
      return of([]);
    }

    this.loading.startTask();
    return forkJoin(uploadRequests).pipe(
      finalize(() => this.loading.endTask())
    );
  }
}
